import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import type { Guid, IAddressInfo, IAgencyInfo, IEntityStatusInfo, IEntityStatusWorkflowInfo, IProductInfo, ITeamInfo, ITicketDeliveryInfo, ITicketDeliveryProductInfo, ITicketInfo, IUserInfo, ISubDivisionInfo, IUnitInfo, IEquipmentVehicleInfo as IEquipmentInfo, IEquipmentModelInfo, IEquipmentTypeInfo } from "@kiraah-shared/common";
import { Address, EntityStatus, EntityStatusWorkflow, EnumHelper, EquipmentModel, EquipmentType, EquipmentVehicle, LvfAppModule, LvfDeliveryMode, LvfHandlingEnergyType, LvfProductNature, LvfTicketArticleNature, Product, SearchFilterBase, Ticket, TicketDelivery, TicketDeliveryProduct, Unit } from "@kiraah-shared/common";
import { IModuleTabConfigInfo, IModuleTabItemInfo, IModuleWizardConfig, IWizardStepConfig, ModuleTabConfig, ModuleTabItem, WizardNavigationService } from "@kiraah-shared/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subject, debounceTime, firstValueFrom, merge } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { formatTicketDeliveryAddressLines } from "../helpers/mobile-ticket-address-format";
import { mobileDeliveryProductSubtitle, mobileDeliveryProductTitle } from "../helpers/mobile-ticket-delivery-line";
import { mobileTicketRecipientLabel, mobileTicketRequesterLabel, mobileTicketTypeDisplayLabel, mobileTicketTypeMaterialIcon } from "../helpers/mobile-ticket-demand-display";
import { buildTicketDemandWizardSteps, mapMobileTicketWizardStepToWebKey } from "../helpers/mobile-ticket-wizard-steps.model";
import { MobileModuleTabComponent } from "../mobile-module-tab/mobile-module-tab.component";
import { mobileEntityStatusDotColor } from "../../mission-task/helpers/mobile-mission-task-display";
import { MobileFleetApiClient } from "../../fleet/services/mobile-fleet-api.client";
import { MobileTicketApiClient } from "../services/mobile-ticket-api.client";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileTicketGeneralInfoStepComponent } from "./steps/ticket-general-info/mobile-ticket-general-info-step.component";
import { MobileTicketProductSelectionStepComponent } from "./steps/ticket-product-selection/mobile-ticket-product-selection-step.component";
import { MobileTicketDeliveryAddressStepComponent } from "./steps/ticket-delivery-address/mobile-ticket-delivery-address-step.component";
import { MobileTicketEquipmentTypesStepComponent } from "./steps/ticket-vehicle-types/mobile-ticket-vehicle-types-step.component";
import { MobileTicketEquipmentSelectionStepComponent } from "./steps/ticket-vehicle-selection/mobile-ticket-vehicle-selection-step.component";
import { MobileTicketSummaryStepComponent } from "./steps/ticket-summary/mobile-ticket-summary-step.component";
import { MobileProductDetailSheetComponent } from "./mobile-product-detail-sheet/mobile-product-detail-sheet.component";

type RequesterMode = "teams" | "identities" | "subdivisions";
type RecipientMode = "teams" | "identities";

@Component({
  selector: "mobile-ticket-wizard",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    MobileModuleTabComponent,
    MobileTicketGeneralInfoStepComponent,
    MobileTicketProductSelectionStepComponent,
    MobileProductDetailSheetComponent,
    MobileTicketEquipmentTypesStepComponent,
    MobileTicketEquipmentSelectionStepComponent,
    MobileTicketDeliveryAddressStepComponent,
    MobileTicketSummaryStepComponent,
  ],
  templateUrl: "./mobile-ticket-wizard-page.component.html",
  styleUrl: "./mobile-ticket-wizard-page.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileTicketWizardComponent {
  readonly ticketWizardHost = this;

  private readonly api = inject(MobileTicketApiClient);
  private readonly fleet: MobileFleetApiClient = inject(MobileFleetApiClient);
  private readonly translateSvc = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleAccess = inject(MobileModuleAccessService);

  readonly ticketAccess = this.moduleAccess.eachatTicketAccess;

  readonly canSubmitTicket = computed(() => {
    const access = this.ticketAccess();
    const uid = this.route.snapshot.paramMap.get("ticketUid");
    return uid ? access.hasUpdateAccess : access.hasCreateAccess;
  });

  readonly ticketFormReadOnly = computed(() => {
    const uid = this.route.snapshot.paramMap.get("ticketUid");
    return !!uid && !this.ticketAccess().hasUpdateAccess;
  });

  readonly loadStatus = signal<"idle" | "loading" | "error" | "ready">("loading");
  readonly saveStatus = signal<"idle" | "saving" | "error">("idle");
  readonly draft = signal<Ticket | null>(null);
  readonly ticketModuleConfig = signal<ModuleTabConfig | null>(null);
  readonly activeStepIndex = signal(0);

  readonly requesterSelectionMode = signal<RequesterMode>("teams");
  readonly recipientSelectionMode = signal<RecipientMode>("teams");
  readonly requesterSearchSig = signal("");
  readonly recipientSearchSig = signal("");
  readonly requesterCatalogLoading = signal(false);
  readonly recipientCatalogLoading = signal(false);
  readonly requesterMissingTouched = signal(false);

  readonly requesterTeamsCatalog = signal<ITeamInfo[]>([]);
  readonly requesterIdentitiesCatalog = signal<IUserInfo[]>([]);
  readonly requesterSubDivisionsCatalog = signal<ISubDivisionInfo[]>([]);
  readonly recipientTeamsCatalog = signal<ITeamInfo[]>([]);
  readonly recipientIdentitiesCatalog = signal<IUserInfo[]>([]);
  readonly ticketStatusList = signal<IEntityStatusInfo[]>([]);
  readonly ticketWorkflowInfo = signal<IEntityStatusWorkflowInfo | null>(null);
  readonly productStepPanel = signal<"catalog" | "selection">("catalog");
  readonly productSearchSig = signal("");
  readonly productCatalogList = signal<IProductInfo[]>([]);
  readonly productCatalogTotalCount = signal(0);
  readonly productCatalogLoading = signal(false);
  readonly productCatalogLoadingMore = signal(false);
  readonly unitsCatalog = signal<IUnitInfo[]>([]);
  readonly productsStepMissingLines = signal(false);
  readonly productRowDrafts = signal<Record<string, { quantity: number; unitUid: string }>>({});
  readonly productAddHintKey = signal<"quantity" | "unit" | null>(null);
  readonly selectionHydrating = signal(false);
  readonly productDetailProductUid = signal<Guid | null>(null);
  readonly productDetailPreview = signal<IProductInfo | null>(null);

  /** Catalogue « types équipement » (vue gauche web) — équivalent UX produits. */
  readonly equipmentTypeCatalogPanel = signal<"catalog" | "selection">("catalog");
  readonly equipmentTypeCatalogSearchSig = signal("");
  readonly equipmentTypeCatalogList = signal<IEquipmentTypeInfo[]>([]);
  readonly equipmentTypeCatalogLoading = signal(false);

  readonly fleetCatalogPanel = signal<"catalog" | "selection">("catalog");
  readonly fleetCatalogSearchSig = signal("");
  readonly fleetCatalogList = signal<IEquipmentInfo[]>([]);
  readonly fleetCatalogLoading = signal(false);

  /** Type d’équipement → valeur {@link LvfHandlingEnergyType} (via modèle, comme le web). */
  readonly equipmentHandlingEnergyByTypeUid = signal<Readonly<Record<string, number>>>({});
  readonly equipmentModelsEnergyLoading = signal(false);
  readonly equipmentLinesStepMissing = signal(false);
  private equipmentEnergyModelsFetchedForTicketUid: string | null = null;

  /** Catalogue agences (mode livraison sur site). */
  readonly deliveryAgenciesCatalog = signal<IAgencyInfo[]>([]);
  readonly deliveryAgencyLoading = signal(false);
  readonly deliveryAgencySearchSig = signal("");
  readonly deliverySelectedAgency = signal<IAgencyInfo | null>(null);
  readonly deliveryAddressIncompleteTouched = signal(false);

  private productUnitsTicketUid: string | null = null;
  private productCatalogPage = 1;
  /** Aligné sur le picker web (`ThirdPartContactPicker.defaultPageSize` / `SearchFilterBase`). */
  private readonly productCatalogPageSize = 12;
  private readonly productSearchBus = new Subject<void>();
  private readonly deliveryAgencySearchBus = new Subject<void>();
  private readonly equipmentTypeCatalogSearchBus = new Subject<void>();
  private readonly fleetCatalogSearchBus = new Subject<void>();

  readonly wizardSteps = computed(() => buildTicketDemandWizardSteps(this.draft()));

  /**
   * Identifiant d’instance passé à {@link MobileWizardNavigationComponent} — miroir du web
   * (`wizardInstanceId` + {@link WizardNavigationService}).
   */
  readonly mobileWizardInstanceId = computed(() => {
    const ticket = this.draft();
    if (!ticket?.Uid || this.loadStatus() !== "ready") {
      return null as string | null;
    }
    return this.wizardInstanceIdForTicket(ticket);
  });

  readonly currentStepKey = computed(() => {
    const steps = this.wizardSteps();
    const stepIndex = this.activeStepIndex();
    return steps[stepIndex]?.key ?? "general";
  });

  readonly descriptionMaxLength = 250;

  readonly generalForm = this.fb.nonNullable.group({
    label: ["", [Validators.required, Validators.minLength(2)]],
    description: [""],
  });

  /** Alimente la synchro {@link WizardNavigationService} (validation récap / envoi). */
  private readonly generalFormValid = toSignal(
    merge(this.generalForm.statusChanges, this.generalForm.valueChanges).pipe(
      map(() => this.generalForm.valid),
      startWith(this.generalForm.valid),
    ),
    { initialValue: this.generalForm.valid },
  );

  private readonly wizardNavigation = inject(WizardNavigationService);
  private registeredWizardInstanceId: string | null = null;

  readonly mobileDeliveryProductTitle = mobileDeliveryProductTitle;
  readonly mobileDeliveryProductSubtitle = mobileDeliveryProductSubtitle;
  readonly mobileTicketRequesterLabel = mobileTicketRequesterLabel;
  readonly mobileTicketRecipientLabel = mobileTicketRecipientLabel;
  readonly mobileTicketTypeDisplayLabel = mobileTicketTypeDisplayLabel;
  readonly mobileTicketTypeMaterialIcon = mobileTicketTypeMaterialIcon;
  readonly mobileEntityStatusDotColor = mobileEntityStatusDotColor;

  readonly DeliveryMode = LvfDeliveryMode;

  readonly filteredRequesterTeams = computed(() =>
    this.filterTeamsBySearch(this.requesterTeamsCatalog(), this.requesterSearchSig())
  );

  readonly filteredRequesterIdentities = computed(() =>
    this.filterIdentitiesBySearch(this.requesterIdentitiesCatalog(), this.requesterSearchSig())
  );

  readonly filteredRequesterSubDivisions = computed(() =>
    this.filterSubDivisionsBySearch(this.requesterSubDivisionsCatalog(), this.requesterSearchSig())
  );

  readonly filteredRecipientTeams = computed(() =>
    this.filterTeamsBySearch(this.recipientTeamsCatalog(), this.recipientSearchSig())
  );

  readonly filteredRecipientIdentities = computed(() =>
    this.filterIdentitiesBySearch(this.recipientIdentitiesCatalog(), this.recipientSearchSig())
  );

  readonly showRequesterError = computed(() => {
    const ticket = this.draft();
    return this.requesterMissingTouched() && !!ticket && !this.hasRequester(ticket);
  });

  readonly ticketSelectedStatusUidStr = computed(() => {
    const ticket = this.draft();
    const statusUid = ticket?.EntityStatusUid ?? ticket?.EntityStatus?.Uid;
    return statusUid != null && String(statusUid) !== "" ? String(statusUid) : "";
  });

  readonly ticketStatusSummary = computed(() => {
    const entityStatus = this.draft()?.EntityStatus;
    if (!entityStatus?.Label) {
      return null;
    }
    return {
      label: entityStatus.Label,
      color: mobileEntityStatusDotColor(entityStatus.ColorHexa ?? null),
    };
  });

  readonly deliveryCartCount = computed(() => this.deliveryProducts().length);

  readonly deliveryManualAddress = computed(() => {
    const delivery = this.draft()?.Delivery;
    if (delivery?.LvfDeliveryMode !== LvfDeliveryMode.MANUAL_INPUT_ADDRESS) {
      return null;
    }
    return delivery.EntityAddress ?? null;
  });

  readonly addressStepBlocksFooterNext = computed(() => {
    if (this.currentStepKey() !== "address") {
      return false;
    }
    return !this.isTicketDeliveryAddressReady(this.draft());
  });

  readonly showSaveAsDraftButton = computed(() => {
    const ticket = this.draft();
    if (!ticket || this.loadStatus() !== "ready") {
      return false;
    }
    const wfInfo = this.ticketWorkflowInfo();
    if (!wfInfo?.Statuses?.length) {
      return true;
    }
    const workflow = EntityStatusWorkflow.createInstance(EntityStatusWorkflow, wfInfo);
    const initialStatusUid = workflow.getInitialStatus()?.Uid;
    if (!ticket.EntityStatusUid) {
      return true;
    }
    return ticket.EntityStatusUid === initialStatusUid;
  });

  readonly sortedUnits = computed(() =>
    [...this.unitsCatalog()].sort((a, b) =>
      (a.Name ?? "").localeCompare(b.Name ?? "", undefined, { sensitivity: "base" }),
    ),
  );

  constructor() {
    this.productSearchBus
      .pipe(debounceTime(420), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        untracked(() => void this.loadProductCatalog());
      });

    this.equipmentTypeCatalogSearchBus.pipe(debounceTime(420), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      untracked(() => void this.loadEquipmentTypeCatalog());
    });

    this.fleetCatalogSearchBus.pipe(debounceTime(420), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      untracked(() => void this.loadFleetEquipmentCatalog());
    });

    this.deliveryAgencySearchBus.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      untracked(() => void this.loadDeliveryAgenciesCatalog());
    });

    effect(() => {
      const loading = this.loadStatus();
      const idx = this.activeStepIndex();
      if (loading !== "ready") {
        return;
      }
      untracked(() => {
        const ticket = this.draft();
        if (!ticket) {
          return;
        }
        const steps = buildTicketDemandWizardSteps(ticket);
        const stepKey = steps[idx]?.key;
        if (stepKey === "address") {
          void this.initializeAddressStep();
          return;
        }
        if (stepKey === "summary") {
          void this.enrichDraftDeliveryProducts();
          if (ticket.TicketType?.LvfTicketArticleNature === LvfTicketArticleNature.VEHICLE_TYPE) {
            void this.ensureEquipmentModelsForEnergyLoaded();
          }
          return;
        }
        if (stepKey !== "products") {
          this.productStepPanel.set("catalog");
        }
        if (stepKey !== "equipment-types") {
          this.equipmentTypeCatalogPanel.set("catalog");
        }
        if (stepKey !== "equipment-selection") {
          this.fleetCatalogPanel.set("catalog");
        }
        if (stepKey === "equipment-types") {
          void this.ensureEquipmentModelsForEnergyLoaded();
          void this.initializeEquipmentWizardStep();
          void this.loadEquipmentTypeCatalog();
          return;
        }
        if (stepKey === "equipment-selection") {
          void this.initializeEquipmentWizardStep();
          void this.loadFleetEquipmentCatalog();
          return;
        }
        if (stepKey === "products") {
          void this.initializeProductsStep();
        }
      });
    });

    effect(() => {
      this.activeStepIndex();
      this.draft();
      this.ticketWorkflowInfo();
      untracked(() => {
        const moduleConfig = this.ticketModuleConfig();
        if (!moduleConfig) {
          return;
        }
        moduleConfig.ActualWidgetIndex = this.activeStepIndex();
        if (moduleConfig.Wizard) {
          moduleConfig.Wizard.ShowSaveAsDraftButton = this.getShowSaveAsDraftForWizardConfig();
        }
      });
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pm) => {
      const uid = pm.get("ticketUid");
      void this.loadTicket(uid);
    });

    this.destroyRef.onDestroy(() => {
      this.unregisterMobileWizardNavigation();
    });

    effect(() => {
      this.activeStepIndex();
      this.addressStepBlocksFooterNext();
      this.generalFormValid();
      this.showSaveAsDraftButton();
      this.mobileWizardInstanceId();
      untracked(() => this.syncMobileWizardNavigationState());
    });
  }

  private wizardInstanceIdForTicket(ticket: Ticket): string {
    return `mobile_ticket_wizard_${String(ticket.Uid)}`;
  }

  private buildTicketModuleTabConfig(ticket: Ticket): ModuleTabConfig {
    const wid = this.wizardInstanceIdForTicket(ticket);
    const metaSteps = buildTicketDemandWizardSteps(ticket);
    const linesOk = (ticket.Delivery?.Products?.length ?? 0) > 0;
    const items: ModuleTabItem[] = [];
    for (const metaStep of metaSteps) {
      items.push(new ModuleTabItem(this.ticketModuleTabItemInfoForStep(metaStep.key, linesOk)));
    }
    return new ModuleTabConfig(<IModuleTabConfigInfo>{
      ActualWidgetIndex: this.activeStepIndex(),
      Items: items,
      Widgets: [],
      Layout: "timeline-top",
      Wizard: <IModuleWizardConfig>{
        Title: "app.ticket.wizard.title",
        Subtitle: "app.ticket.wizard.subtitle",
        Icon: "assignment",
        ShowNavigationButtons: true,
        WizardInstanceId: wid,
        InitialEntityData: ticket,
        ShowSaveAsDraftButton: this.getShowSaveAsDraftForWizardConfig(),
        ButtonTexts: {
          Complete: "app.ticket.wizard.submit",
          SaveAsDraft: "app.wizard.saveasdraft",
        },
      },
    });
  }

  private ticketModuleTabItemInfoForStep(mobileKey: string, linesOk: boolean): IModuleTabItemInfo {
    const base: IModuleTabItemInfo = {
      Key: "generalinfo",
      Label: "app.ticket.wizard.step.generalinfo",
      IsLongLabel: false,
      LongLabel: "",
      IsNumberDisplayed: false,
      Number: 0,
      Icon: "person",
      IsSkippable: false,
      CanGoPrevious: true,
      CanGoNext: true,
    };
    switch (mobileKey) {
      case "general":
        return { ...base, Key: "generalinfo", Label: "app.ticket.wizard.step.generalinfo", Icon: "person" };
      case "products":
        return {
          ...base,
          Key: "productselection",
          Label: "app.ticket.wizard.step.productselection",
          Icon: "shopping_cart",
          CanGoNext: linesOk,
        };
      case "equipment-types":
        return {
          ...base,
          Key: "equipmenttypeselection",
          Label: "app.ticket.wizard.step.equipmenttypeselection",
          Icon: "directions_car",
          CanGoNext: linesOk,
        };
      case "equipment-selection":
        return {
          ...base,
          Key: "vehicleselection",
          Label: "app.ticket.wizard.step.vehicleselection",
          Icon: "directions_car",
          CanGoNext: linesOk,
        };
      case "address":
        return {
          ...base,
          Key: "deliveryaddress",
          Label: "app.ticket.wizard.step.deliveryaddress",
          Icon: "location_on",
        };
      case "summary":
        return { ...base, Key: "summary", Label: "app.ticket.wizard.step.summary", Icon: "summarize" };
      default:
        return base;
    }
  }

  private getShowSaveAsDraftForWizardConfig(): boolean {
    const ticket = this.draft();
    if (!ticket || this.loadStatus() !== "ready") {
      return false;
    }
    const wfInfo = this.ticketWorkflowInfo();
    if (!wfInfo?.Statuses?.length) {
      return true;
    }
    const workflow = EntityStatusWorkflow.createInstance(EntityStatusWorkflow, wfInfo);
    const initialStatusUid = workflow.getInitialStatus()?.Uid;
    if (!ticket.EntityStatusUid) {
      return true;
    }
    return ticket.EntityStatusUid === initialStatusUid;
  }

  private unregisterMobileWizardNavigation(): void {
    if (this.registeredWizardInstanceId) {
      this.wizardNavigation.unregisterWizardInstance(this.registeredWizardInstanceId);
      this.registeredWizardInstanceId = null;
    }
    this.ticketModuleConfig.set(null);
  }

  private registerMobileWizardNavigation(ticket: Ticket): void {
    const wid = this.wizardInstanceIdForTicket(ticket);
    this.ticketModuleConfig.set(this.buildTicketModuleTabConfig(ticket));
    if (this.wizardNavigation.hasWizardInstance(wid)) {
      this.registeredWizardInstanceId = wid;
      this.syncMobileWizardNavigationState();
      return;
    }
    const steps = buildTicketDemandWizardSteps(ticket);
    const stepsConfig: IWizardStepConfig[] = steps.map((s, i) => ({
      key: s.key,
      label: s.labelKey,
      icon: "chevron_right",
      isSkippable: false,
      canGoNext: true,
      canGoPrevious: i > 0,
      isCompleted: false,
    }));
    this.wizardNavigation.registerWizardInstance(
      {
        wizardInstanceId: wid,
        entityType: "Ticket",
        currentStep: this.activeStepIndex(),
        totalSteps: steps.length,
        canGoNext: true,
        canGoPrevious: this.activeStepIndex() > 0,
        canSkip: false,
        canComplete: this.generalForm.valid,
      },
      stepsConfig,
    );
    this.registeredWizardInstanceId = wid;
    this.syncMobileWizardNavigationState();
  }

  private syncMobileWizardNavigationState(): void {
    const wizardInstanceId = this.registeredWizardInstanceId;
    if (!wizardInstanceId || !this.wizardNavigation.hasWizardInstance(wizardInstanceId)) {
      return;
    }
    const moduleConfig = this.ticketModuleConfig();
    if (moduleConfig?.Wizard) {
      moduleConfig.Wizard.ShowSaveAsDraftButton = this.getShowSaveAsDraftForWizardConfig();
    }
    const currentStepIndex = this.activeStepIndex();
    this.wizardNavigation.setCurrentStep(wizardInstanceId, currentStepIndex);
    this.wizardNavigation.updateCanGoNext(wizardInstanceId, !this.addressStepBlocksFooterNext());
    this.wizardNavigation.updateCanComplete(wizardInstanceId, this.generalForm.valid);
    this.wizardNavigation.updateCanSaveAsDraft(wizardInstanceId, this.getShowSaveAsDraftForWizardConfig());
  }

  ticketTypeColumnLabel(ticket: Ticket): string {
    return ticket.TicketType?.Label?.trim() || mobileTicketTypeDisplayLabel(ticket);
  }

  ticketRequesterInitials(ticket: Ticket): string {
    const name = mobileTicketRequesterLabel(ticket).trim();
    if (!name) {
      return "?";
    }
    const parts = name.split(/\s+/).filter((word) => word.length > 0);
    if (parts.length >= 2) {
      const firstInitial = parts[0]![0] ?? "";
      const lastInitial = parts[parts.length - 1]![0] ?? "";
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }
    return name.length >= 2 ? name.slice(0, 2).toUpperCase() : name.toUpperCase();
  }

  requesterPlaceholderKey(): string {
    const requesterMode = this.requesterSelectionMode();
    if (requesterMode === "teams") {
      return "app.ticket.requester.selectservice";
    }
    if (requesterMode === "identities") {
      return "app.ticket.requester.selectagent";
    }
    return "app.ticket.requester.selectentity";
  }

  recipientPlaceholderKey(): string {
    return this.recipientSelectionMode() === "teams"
      ? "app.ticket.recipient.selectservice"
      : "app.ticket.recipient.selectagent";
  }

  identityRowLabel(row: IUserInfo): string {
    return row.FullName?.trim() || row.Email?.trim() || row.FirstName?.trim() || "?";
  }

  teamTrack(index: number, row: ITeamInfo): string {
    return row.Uid != null ? String(row.Uid) : `t-${index}`;
  }

  identityTrack(index: number, row: IUserInfo): string {
    return row.Uid != null ? String(row.Uid) : `i-${index}`;
  }

  subDivisionTrack(index: number, row: ISubDivisionInfo): string {
    return row.Uid != null ? String(row.Uid) : `sd-${index}`;
  }

  hasRequester(ticket: Ticket): boolean {
    return !!(ticket.RequesterTeamUid || ticket.RequesterIdentityUid || ticket.SubDivisionUid);
  }

  requesterModeToggleMaterialIcon(): string {
    const requesterMode = this.requesterSelectionMode();
    if (requesterMode === "teams") {
      return "person";
    }
    if (requesterMode === "identities") {
      return "account_tree";
    }
    return "business";
  }

  onRequesterToggle(event: Event): void {
    const detailsElement = event.target as HTMLDetailsElement;
    if (detailsElement.open) {
      void this.ensureRequesterCatalogForMode(this.requesterSelectionMode());
    }
  }

  onRecipientToggle(event: Event): void {
    const detailsElement = event.target as HTMLDetailsElement;
    if (detailsElement.open) {
      void this.ensureRecipientCatalogForMode(this.recipientSelectionMode());
    }
  }

  async toggleRequesterSelectionMode(): Promise<void> {
    const cur = this.requesterSelectionMode();
    const next: RequesterMode =
      cur === "teams" ? "identities" : cur === "identities" ? "subdivisions" : "teams";
    this.requesterSelectionMode.set(next);
    this.requesterSearchSig.set("");
    this.clearRequesterSelectionForModeSwitch();
    await this.ensureRequesterCatalogForMode(next);
  }

  async toggleRecipientSelectionMode(): Promise<void> {
    const next: RecipientMode = this.recipientSelectionMode() === "teams" ? "identities" : "teams";
    this.recipientSelectionMode.set(next);
    this.recipientSearchSig.set("");
    this.clearRecipientSelectionForModeSwitch();
    await this.ensureRecipientCatalogForMode(next);
  }

  pickRequesterTeam(team: ITeamInfo, details: HTMLDetailsElement): void {
    this.patchDraft({
      RequesterTeamUid: team.Uid,
      RequesterTeam: team,
      RequesterIdentityUid: undefined,
      RequesterIdentity: undefined,
      SubDivisionUid: undefined,
      SubDivision: undefined,
    });
    details.open = false;
    this.requesterMissingTouched.set(false);
  }

  pickRequesterIdentity(identity: IUserInfo, details: HTMLDetailsElement): void {
    this.patchDraft({
      RequesterIdentityUid: identity.Uid,
      RequesterIdentity: identity,
      RequesterTeamUid: undefined,
      RequesterTeam: undefined,
      SubDivisionUid: undefined,
      SubDivision: undefined,
    });
    details.open = false;
    this.requesterMissingTouched.set(false);
  }

  pickRequesterSubDivision(subDivision: ISubDivisionInfo, details: HTMLDetailsElement): void {
    this.patchDraft({
      SubDivisionUid: subDivision.Uid,
      SubDivision: subDivision,
      RequesterTeamUid: undefined,
      RequesterTeam: undefined,
      RequesterIdentityUid: undefined,
      RequesterIdentity: undefined,
    });
    details.open = false;
    this.requesterMissingTouched.set(false);
  }

  pickRecipientTeam(team: ITeamInfo, details: HTMLDetailsElement): void {
    const ticket = this.draft();
    if (!ticket) {
      return;
    }
    const merged = this.mergeDeliveryPatch(
      ticket.Delivery as unknown as ITicketDeliveryInfo | undefined,
      {
        RecipientTeamUid: team.Uid,
        RecipientTeam: team,
        RecipientIdentityUid: undefined,
        RecipientIdentity: undefined,
      }
    );
    this.patchDraft({ Delivery: merged });
    details.open = false;
  }

  pickRecipientIdentity(identity: IUserInfo, details: HTMLDetailsElement): void {
    const ticket = this.draft();
    if (!ticket) {
      return;
    }
    const merged = this.mergeDeliveryPatch(
      ticket.Delivery as unknown as ITicketDeliveryInfo | undefined,
      {
        RecipientIdentityUid: identity.Uid,
        RecipientIdentity: identity,
        RecipientTeamUid: undefined,
        RecipientTeam: undefined,
      }
    );
    this.patchDraft({ Delivery: merged });
    details.open = false;
  }

  pickTicketStatus(status: IEntityStatusInfo, details: HTMLDetailsElement): void {
    if (!status.Uid) {
      return;
    }
    this.patchDraft({
      EntityStatusUid: status.Uid,
      EntityStatus: status,
    });
    details.open = false;
  }

  ticketStatusTrack(index: number, status: IEntityStatusInfo): string {
    return status.Uid != null ? String(status.Uid) : `st-${index}`;
  }

  ticketStatusValue(status: IEntityStatusInfo): string {
    return status.Uid != null ? String(status.Uid) : "";
  }

  ticketStatusRowDotColor(status: IEntityStatusInfo): string {
    return mobileEntityStatusDotColor(status.ColorHexa ?? null);
  }

  clearRecipient(details: HTMLDetailsElement): void {
    const ticket = this.draft();
    if (!ticket) {
      return;
    }
    const merged = this.mergeDeliveryPatch(ticket.Delivery as unknown as ITicketDeliveryInfo | undefined, {
      RecipientTeamUid: undefined,
      RecipientTeam: undefined,
      RecipientIdentityUid: undefined,
      RecipientIdentity: undefined,
    });
    this.patchDraft({ Delivery: merged });
    details.open = false;
  }

  descriptionCharsRemaining(): number {
    const len = (this.generalForm.controls.description.value ?? "").length;
    return Math.max(0, this.descriptionMaxLength - len);
  }

  formatAddressLines(): string[] {
    const ticket = this.draft();
    return formatTicketDeliveryAddressLines(ticket?.Delivery?.EntityAddress);
  }

  isTicketDeliveryAddressReady(ticket: Ticket | null | undefined): boolean {
    const delivery = ticket?.Delivery;
    if (!delivery) {
      return false;
    }
    const mode = delivery.LvfDeliveryMode ?? LvfDeliveryMode.DIRECT_TO_REQUESTER;
    if (mode === LvfDeliveryMode.MANUAL_INPUT_ADDRESS) {
      return delivery.EntityAddress?.isValid() === true;
    }
    const uid = delivery.EntityAddressUid;
    return uid != null && String(uid).trim() !== "";
  }

  setDeliveryMode(mode: LvfDeliveryMode): void {
    const ticket = this.draft();
    if (!ticket?.Delivery) {
      return;
    }
    if (mode !== LvfDeliveryMode.SPECIFIED_AGENCY) {
      this.deliverySelectedAgency.set(null);
    }
    const interim: Partial<ITicketDeliveryInfo> = { LvfDeliveryMode: mode };
    if (mode === LvfDeliveryMode.MANUAL_INPUT_ADDRESS) {
      interim.EntityAddressUid = undefined;
      const entityAddress = ticket.Delivery.EntityAddress;
      if (entityAddress && !entityAddress.Uid) {
        interim.EntityAddress = entityAddress as Address;
      } else {
        interim.EntityAddress = Address.createInstance({ CountryCode: Address.DEFAULT_COUNTRY });
      }
    }
    const mergedInfo = this.mergeDeliveryPatch(ticket.Delivery as ITicketDeliveryInfo, interim);
    this.patchDraft({ Delivery: TicketDelivery.createInstance(TicketDelivery, mergedInfo) });
    if (mode === LvfDeliveryMode.SPECIFIED_AGENCY) {
      void this.loadDeliveryAgenciesCatalog();
    }
    this.applyComputedDeliveryAddress();
    this.deliveryAddressIncompleteTouched.set(false);
  }

  isDeliveryModeSelected(mode: LvfDeliveryMode): boolean {
    const selectedMode = this.draft()?.Delivery?.LvfDeliveryMode ?? LvfDeliveryMode.DIRECT_TO_REQUESTER;
    return selectedMode === mode;
  }

  pickDeliveryAgency(agency: IAgencyInfo): void {
    this.deliverySelectedAgency.set(agency);
    this.applyComputedDeliveryAddress();
    this.deliveryAddressIncompleteTouched.set(false);
  }

  isDeliveryAgencyRowSelected(agency: IAgencyInfo): boolean {
    const sel = this.deliverySelectedAgency();
    return sel?.Uid != null && agency.Uid != null && String(sel.Uid) === String(agency.Uid);
  }

  deliveryAgencyTrack(index: number, agency: IAgencyInfo): string {
    return agency.Uid != null ? String(agency.Uid) : `a-${index}`;
  }

  onDeliveryAgencySearchChange(value: string): void {
    this.deliveryAgencySearchSig.set(value);
    this.deliveryAgencySearchBus.next();
  }

  patchManualAddress(part: Partial<IAddressInfo>): void {
    const ticket = this.draft();
    const delivery = ticket?.Delivery;
    if (!ticket || !delivery || delivery.LvfDeliveryMode !== LvfDeliveryMode.MANUAL_INPUT_ADDRESS) {
      return;
    }
    const currentAddress = delivery.EntityAddress ?? Address.createInstance({ CountryCode: Address.DEFAULT_COUNTRY });
    const countryCode = String(part.CountryCode ?? currentAddress.CountryCode ?? Address.DEFAULT_COUNTRY);
    const next = Address.createInstance({
      ...(currentAddress as IAddressInfo),
      ...part,
      CountryCode: countryCode,
    });
    const mergedDelivery = TicketDelivery.createInstance(TicketDelivery, {
      ...(delivery as ITicketDeliveryInfo),
      EntityAddress: next,
      EntityAddressUid: undefined,
    } as ITicketDeliveryInfo);
    this.patchDraft({ Delivery: mergedDelivery });
    this.deliveryAddressIncompleteTouched.set(false);
  }

  previewDeliveryResolvedAddressLines(): string[] {
    const ticket = this.draft();
    const delivery = ticket?.Delivery;
    if (!delivery || delivery.LvfDeliveryMode === LvfDeliveryMode.MANUAL_INPUT_ADDRESS) {
      return [];
    }
    return formatTicketDeliveryAddressLines(delivery.EntityAddress ?? undefined);
  }

  private async initializeAddressStep(): Promise<void> {
    await this.loadDeliveryAgenciesCatalog();
    this.trySyncSelectedAgencyFromDraft();
    this.applyComputedDeliveryAddress();
  }

  private async loadDeliveryAgenciesCatalog(): Promise<void> {
    this.deliveryAgencyLoading.set(true);
    try {
      const filter = this.buildRefFilter();
      filter.SearchInput = this.deliveryAgencySearchSig().trim();
      const rows = await firstValueFrom(this.api.filterAgencies(filter));
      this.deliveryAgenciesCatalog.set(rows ?? []);
      this.trySyncSelectedAgencyFromDraft();
    } catch {
      this.deliveryAgenciesCatalog.set([]);
    } finally {
      this.deliveryAgencyLoading.set(false);
    }
  }

  private trySyncSelectedAgencyFromDraft(): void {
    const ticket = this.draft();
    if (ticket?.Delivery?.LvfDeliveryMode !== LvfDeliveryMode.SPECIFIED_AGENCY) {
      return;
    }
    const targetUid = ticket.Delivery.EntityAddressUid;
    if (targetUid == null || String(targetUid) === "") {
      return;
    }
    const found = this.deliveryAgenciesCatalog().find(
      (agency) => agency.Address?.Uid != null && String(agency.Address.Uid) === String(targetUid),
    );
    if (found) {
      this.deliverySelectedAgency.set(found);
    }
  }

  private applyComputedDeliveryAddress(): void {
    const ticket = this.draft();
    if (!ticket?.Delivery) {
      return;
    }
    const mode = ticket.Delivery.LvfDeliveryMode ?? LvfDeliveryMode.DIRECT_TO_REQUESTER;
    const addr = this.computeDeliveryAddressForMode(mode);
    const patch: Partial<ITicketDeliveryInfo> = {
      EntityAddress: addr,
      EntityAddressUid: mode === LvfDeliveryMode.MANUAL_INPUT_ADDRESS ? undefined : addr.Uid,
    };
    const mergedInfo = this.mergeDeliveryPatch(ticket.Delivery as ITicketDeliveryInfo, patch);
    this.patchDraft({ Delivery: TicketDelivery.createInstance(TicketDelivery, mergedInfo) });
  }

  private computeDeliveryAddressForMode(mode: LvfDeliveryMode): Address {
    const ticket = this.draft();
    if (!ticket?.Delivery) {
      return Address.createInstance({});
    }
    const delivery = ticket.Delivery;
    switch (mode) {
      case LvfDeliveryMode.DIRECT_TO_REQUESTER: {
        const src =
          ticket.RequesterTeamUid != null
            ? ticket.RequesterTeam?.Agency?.Address
            : ticket.RequesterIdentity?.MainAgency?.Address;
        return Address.createInstance((src ?? {}) as IAddressInfo);
      }
      case LvfDeliveryMode.DIRECT_TO_RECIPIENT: {
        const src =
          delivery.RecipientTeamUid != null
            ? delivery.RecipientTeam?.Agency?.Address
            : delivery.RecipientIdentity?.MainAgency?.Address;
        return Address.createInstance((src ?? {}) as IAddressInfo);
      }
      case LvfDeliveryMode.SPECIFIED_AGENCY: {
        const selectedAgency = this.deliverySelectedAgency();
        return Address.createInstance((selectedAgency?.Address ?? {}) as IAddressInfo);
      }
      case LvfDeliveryMode.MANUAL_INPUT_ADDRESS: {
        const entityAddress = delivery.EntityAddress;
        if (entityAddress && !entityAddress.Uid) {
          return entityAddress;
        }
        return Address.createInstance({ CountryCode: Address.DEFAULT_COUNTRY });
      }
      default:
        return Address.createInstance({});
    }
  }

  deliveryProducts(): TicketDeliveryProduct[] {
    return this.draft()?.Delivery?.Products ?? [];
  }

  productCatalogTrack(index: number, product: IProductInfo): string {
    return product.Uid != null ? String(product.Uid) : `p-${index}`;
  }

  catalogProductUid(product: IProductInfo): string {
    return product.Uid != null ? String(product.Uid) : "";
  }

  unitOptionTrack(index: number, unit: IUnitInfo): string {
    return unit.Uid != null ? String(unit.Uid) : `u-${index}`;
  }

  onProductSearchChange(value: string): void {
    this.productSearchSig.set(value);
    this.productSearchBus.next();
  }

  productCatalogHasMore(): boolean {
    return this.productCatalogList().length < this.productCatalogTotalCount();
  }

  loadMoreProductCatalog(): void {
    if (this.productCatalogLoadingMore() || !this.productCatalogHasMore()) {
      return;
    }
    this.productCatalogPage += 1;
    void this.fetchProductCatalog(false);
  }

  getProductRowDraft(productUid: string): { quantity: number; unitUid: string } {
    const productDraftMap = this.productRowDrafts();
    return productDraftMap[productUid] ?? { quantity: 1, unitUid: "" };
  }

  patchProductRowDraft(productUid: string, part: Partial<{ quantity: number; unitUid: string }>): void {
    this.productRowDrafts.update((cur) => {
      const prev = cur[productUid] ?? { quantity: 1, unitUid: "" };
      return { ...cur, [productUid]: { ...prev, ...part } };
    });
    this.productAddHintKey.set(null);
  }

  parseQuantityInput(rawValue: unknown): number {
    const numericValue = typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!Number.isFinite(numericValue) || numericValue < 1) {
      return 0;
    }
    return Math.floor(numericValue);
  }

  bumpCatalogProductDraftQuantity(productUid: string | undefined | null, delta: number): void {
    const uid = String(productUid ?? "").trim();
    if (!uid || !delta) {
      return;
    }
    const row = this.getProductRowDraft(uid);
    const currentQuantity = Number(row.quantity ?? 1);
    const next = Math.max(1, Math.floor(currentQuantity + delta));
    if (next === currentQuantity) {
      return;
    }
    this.patchProductRowDraft(uid, { quantity: next });
  }

  adjustSelectionLineQuantity(line: TicketDeliveryProduct, delta: number): void {
    if (!line.ProductUid || !delta) {
      return;
    }
    const currentQuantity = Number(line.Quantity ?? 1);
    const next = Math.max(1, Math.floor(currentQuantity + delta));
    if (next === currentQuantity) {
      return;
    }
    this.updateSelectionLineQuantity(line, next);
  }

  unitUidStr(unit: IUnitInfo): string {
    return unit.Uid != null ? String(unit.Uid) : "";
  }

  openProductSelectionPanel(): void {
    this.productStepPanel.set("selection");
    void this.prepareProductSelectionPanel();
  }

  closeProductSelectionPanel(): void {
    this.productStepPanel.set("catalog");
    void this.loadProductCatalog();
  }

  openProductDetail(product: IProductInfo): void {
    if (product.Uid == null || String(product.Uid) === "") {
      return;
    }

    this.productDetailProductUid.set(product.Uid);
    this.productDetailPreview.set(product);
  }

  closeProductDetail(): void {
    this.productDetailProductUid.set(null);
    this.productDetailPreview.set(null);
  }

  onProductDetailSaved(productInfo: IProductInfo): void {
    const refreshedProduct = Product.createInstance(Product, productInfo);
    const refreshedUid = refreshedProduct.Uid != null ? String(refreshedProduct.Uid) : "";
    if (!refreshedUid) {
      this.closeProductDetail();
      return;
    }

    this.productCatalogList.update((products) =>
      products.map((product) =>
        String(product.Uid ?? "") === refreshedUid
          ? { ...product, ...productInfo }
          : product,
      ),
    );

    const ticket = this.draft();
    if (ticket?.Delivery?.Products?.length) {
      const nextLines = ticket.Delivery.Products.map((line) => {
        if (String(line.ProductUid ?? "") !== refreshedUid) {
          return line;
        }

        return TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
          ...(line as unknown as ITicketDeliveryProductInfo),
          Product: refreshedProduct,
          ProductLabel: refreshedProduct.Label ?? line.ProductLabel,
        } as ITicketDeliveryProductInfo);
      });

      const nextDelivery = TicketDelivery.createInstance(TicketDelivery, {
        ...(ticket.Delivery as ITicketDeliveryInfo),
        Products: nextLines,
      } as ITicketDeliveryInfo);

      this.patchDraft({ Delivery: nextDelivery });
    }

    this.closeProductDetail();
    void this.loadProductCatalog();
  }

  selectionLineTrack(index: number, line: TicketDeliveryProduct): string {
    const uid = line.Uid != null && String(line.Uid) !== "" ? String(line.Uid) : "";
    if (uid) {
      return `dl-${uid}`;
    }
    if (line.ProductUid != null && String(line.ProductUid) !== "") {
      return `p-${String(line.ProductUid)}`;
    }
    if (line.VehicleUid != null && String(line.VehicleUid) !== "") {
      return `v-${String(line.VehicleUid)}`;
    }
    if (line.VehicleTypeUid != null && String(line.VehicleTypeUid) !== "") {
      return `vt-${String(line.VehicleTypeUid)}`;
    }
    return `i-${index}`;
  }

  selectionLineUnitValue(line: TicketDeliveryProduct): string {
    return line.UnitUid != null && String(line.UnitUid) !== "" ? String(line.UnitUid) : "";
  }

  updateSelectionLineQuantity(line: TicketDeliveryProduct, raw: unknown): void {
    const quantity = this.parseQuantityInput(raw);
    if (!line.ProductUid || quantity < 1) {
      return;
    }
    if (line.Quantity === quantity) {
      return;
    }
    const next = TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
      ...(line as unknown as ITicketDeliveryProductInfo),
      Quantity: quantity,
    } as ITicketDeliveryProductInfo);
    this.mergeProductLineIntoDraft(next);
  }

  updateSelectionLineUnit(line: TicketDeliveryProduct, unitUidRaw: string): void {
    if (!line.ProductUid || !unitUidRaw) {
      return;
    }
    const unit = this.unitsCatalog().find((unitCandidate) => String(unitCandidate.Uid) === unitUidRaw);
    if (!unit) {
      return;
    }
    const next = TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
      ...(line as unknown as ITicketDeliveryProductInfo),
      UnitUid: unit.Uid,
      Unit: Unit.createInstance(Unit, unit),
    } as ITicketDeliveryProductInfo);
    this.mergeProductLineIntoDraft(next);
  }

  addCatalogProductLine(product: IProductInfo): void {
    const uid = String(product.Uid ?? "");
    if (!uid) {
      return;
    }
    const { quantity, unitUid } = this.getProductRowDraft(uid);
    if (!quantity || quantity <= 0) {
      this.productAddHintKey.set("quantity");
      return;
    }
    const unit = this.unitsCatalog().find((unitCandidate) => String(unitCandidate.Uid) === unitUid);
    if (!unit) {
      this.productAddHintKey.set("unit");
      return;
    }
    this.productAddHintKey.set(null);
    const line = this.createDeliveryProductLine(product, quantity, unit);
    this.mergeProductLineIntoDraft(line);
    this.productsStepMissingLines.set(false);
    void this.loadProductCatalog();
  }

  removeDeliveryProductLine(productUid: Guid | string): void {
    const ticket = this.draft();
    if (!ticket?.Delivery?.Products?.length) {
      return;
    }
    const uid = String(productUid);
    const prevProducts = ticket.Delivery.Products.filter((productLine) => String(productLine.ProductUid ?? "") !== uid);
    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, {
      ...(ticket.Delivery as ITicketDeliveryInfo),
      Products: prevProducts,
    } as ITicketDeliveryInfo);
    this.patchDraft({ Delivery: nextDelivery });
    void this.loadProductCatalog();
  }

  removeDeliveryLine(line: TicketDeliveryProduct): void {
    const ticket = this.draft();
    if (!ticket?.Delivery?.Products?.length) {
      return;
    }
    const lineUid = line.Uid != null && String(line.Uid) !== "" ? String(line.Uid) : null;
    const prevProducts = ticket.Delivery.Products.filter((productLine) => !this.sameDeliveryBasketLine(line, lineUid, productLine));
    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, {
      ...(ticket.Delivery as ITicketDeliveryInfo),
      Products: prevProducts,
    } as ITicketDeliveryInfo);
    this.patchDraft({ Delivery: nextDelivery });
    this.productsStepMissingLines.set(false);
    this.equipmentLinesStepMissing.set(false);
    void this.loadProductCatalog();
    void this.loadEquipmentTypeCatalog();
    void this.loadFleetEquipmentCatalog();
  }

  onEquipmentTypeCatalogSearchChange(value: string): void {
    this.equipmentTypeCatalogSearchSig.set(value);
    this.equipmentTypeCatalogSearchBus.next();
  }

  onFleetEquipmentCatalogSearchChange(value: string): void {
    this.fleetCatalogSearchSig.set(value);
    this.fleetCatalogSearchBus.next();
  }

  openEquipmentTypeSelectionPanel(): void {
    this.equipmentTypeCatalogPanel.set("selection");
    void this.initializeEquipmentWizardStep();
  }

  closeEquipmentTypeSelectionPanel(): void {
    this.equipmentTypeCatalogPanel.set("catalog");
    void this.loadEquipmentTypeCatalog();
  }

  openFleetEquipmentSelectionPanel(): void {
    this.fleetCatalogPanel.set("selection");
    void this.initializeEquipmentWizardStep();
  }

  closeFleetEquipmentSelectionPanel(): void {
    this.fleetCatalogPanel.set("catalog");
    void this.loadFleetEquipmentCatalog();
  }

  equipmentTypeCatalogTrack(index: number, row: IEquipmentTypeInfo): string {
    return row.Uid != null ? String(row.Uid) : `vt-${index}`;
  }

  equipmentTypeCatalogUid(row: IEquipmentTypeInfo): string {
    return row.Uid != null ? String(row.Uid) : "";
  }

  fleetCatalogTrack(index: number, row: IEquipmentInfo): string {
    return row.Uid != null ? String(row.Uid) : `v-${index}`;
  }

  fleetCatalogEquipmentUid(row: IEquipmentInfo): string {
    return row.Uid != null ? String(row.Uid) : "";
  }

  fleetCatalogEquipmentPrimaryLabel(row: IEquipmentInfo): string {
    const identificationInfo = row.IdentificationInfo?.trim();
    return identificationInfo || this.translateSvc.instant("app.shared.notprovided");
  }

  fleetCatalogEquipmentSecondaryLabel(row: IEquipmentInfo): string {
    return row.RegistrationNumber?.trim() ?? "";
  }

  catalogEquipmentTypeEnergyLabel(row: IEquipmentTypeInfo): string {
    const vtUid = row.Uid != null ? String(row.Uid) : "";
    if (!vtUid) {
      return this.translateSvc.instant("app.shared.notprovided");
    }
    const energyRaw = this.equipmentHandlingEnergyByTypeUid()[vtUid];
    if (energyRaw == null || !Number.isFinite(energyRaw)) {
      return this.translateSvc.instant("app.shared.notprovided");
    }
    return this.translateHandlingEnergyNumeric(energyRaw);
  }

  private translateHandlingEnergyNumeric(energyRaw: number): string {
    const notProvided = this.translateSvc.instant("app.shared.notprovided");
    const normalizedEnergy = Math.trunc(Number(energyRaw));
    if (!Number.isFinite(normalizedEnergy)) {
      return notProvided;
    }
    const memberName = LvfHandlingEnergyType[normalizedEnergy];
    if (memberName == null || typeof memberName !== "string") {
      return notProvided;
    }
    const key = EnumHelper.getTranslationKey("LvfHandlingEnergyType", LvfHandlingEnergyType, normalizedEnergy);
    if (!key || key.endsWith(".")) {
      return notProvided;
    }
    const label = this.translateSvc.instant(key);
    return label === key ? notProvided : label;
  }

  addEquipmentTypeCatalogLine(row: IEquipmentTypeInfo): void {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    if (ticket.TicketType?.LvfTicketArticleNature !== LvfTicketArticleNature.VEHICLE_TYPE) {
      return;
    }
    const uid = row.Uid != null ? String(row.Uid) : "";
    if (!uid) {
      return;
    }
    const equipmentType = EquipmentType.createInstance(EquipmentType, row);
    const line = TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
      TicketDeliveryUid: ticket.Uid,
      EquipmentTypeUid: equipmentType.Uid,
      VehicleTypeUid: equipmentType.Uid,
      EquipmentType: equipmentType,
      VehicleType: equipmentType,
      ProductLabel: equipmentType.Label,
      Quantity: 1,
    } as ITicketDeliveryProductInfo);
    this.mergeEquipmentTypeLineIntoDraft(line);
    this.equipmentLinesStepMissing.set(false);
    void this.loadEquipmentTypeCatalog();
  }

  addFleetCatalogEquipmentLine(row: IEquipmentInfo): void {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    if (ticket.TicketType?.LvfTicketArticleNature !== LvfTicketArticleNature.VEHICLE) {
      return;
    }
    const uid = row.Uid != null ? String(row.Uid) : "";
    if (!uid) {
      return;
    }
    const equipment = new EquipmentVehicle(row);
    const line = TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
      TicketDeliveryUid: ticket.Uid,
      VehicleUid: equipment.Uid,
      EquipmentVehicle: equipment,
      ProductLabel: equipment.RegistrationNumber ?? equipment.IdentificationInfo,
      Quantity: 1,
    } as ITicketDeliveryProductInfo);
    this.mergeFleetEquipmentLineIntoDraft(line);
    this.equipmentLinesStepMissing.set(false);
    void this.loadFleetEquipmentCatalog();
  }

  private mergeEquipmentTypeLineIntoDraft(line: TicketDeliveryProduct): void {
    const ticket = this.draft();
    if (!ticket || line.VehicleTypeUid == null || String(line.VehicleTypeUid) === "") {
      return;
    }
    const key = String(line.VehicleTypeUid);
    const prevDelivery = ticket.Delivery;
    const prevProducts = [...(prevDelivery?.Products ?? [])];
    const idx = prevProducts.findIndex(
      (productLine) =>
        (productLine.ProductUid == null || String(productLine.ProductUid) === "") &&
        String(productLine.VehicleTypeUid ?? "") === key &&
        (productLine.VehicleUid == null || String(productLine.VehicleUid) === ""),
    );
    if (idx >= 0) {
      prevProducts[idx] = line;
    } else {
      prevProducts.push(line);
    }
    const baseInfo: ITicketDeliveryInfo = {
      ...((prevDelivery ?? {}) as ITicketDeliveryInfo),
      Products: prevProducts,
    };
    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, baseInfo);
    this.patchDraft({ Delivery: nextDelivery });
  }

  private mergeFleetEquipmentLineIntoDraft(line: TicketDeliveryProduct): void {
    const ticket = this.draft();
    if (!ticket || line.VehicleUid == null || String(line.VehicleUid) === "") {
      return;
    }
    const key = String(line.VehicleUid);
    const prevDelivery = ticket.Delivery;
    const prevProducts = [...(prevDelivery?.Products ?? [])];
    const idx = prevProducts.findIndex(
      (productLine) =>
        (productLine.ProductUid == null || String(productLine.ProductUid) === "") &&
        String(productLine.VehicleUid ?? "") === key &&
        (productLine.VehicleTypeUid == null || String(productLine.VehicleTypeUid) === ""),
    );
    if (idx >= 0) {
      prevProducts[idx] = line;
    } else {
      prevProducts.push(line);
    }
    const baseInfo: ITicketDeliveryInfo = {
      ...((prevDelivery ?? {}) as ITicketDeliveryInfo),
      Products: prevProducts,
    };
    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, baseInfo);
    this.patchDraft({ Delivery: nextDelivery });
  }

  private async loadEquipmentTypeCatalog(): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    const idx = this.activeStepIndex();
    const steps = buildTicketDemandWizardSteps(ticket);
    if (steps[idx]?.key !== "equipment-types") {
      return;
    }
    if (ticket.TicketType?.LvfTicketArticleNature !== LvfTicketArticleNature.VEHICLE_TYPE) {
      return;
    }

    this.equipmentTypeCatalogLoading.set(true);
    try {
      const filter = SearchFilterBase.CreateInstance();
      filter.Paginate = true;
      filter.Page = 1;
      filter.PageSize = 60;
      filter.OnlyActiveEntities = true;
      filter.UseCache = false;
      filter.SearchInput = this.equipmentTypeCatalogSearchSig().trim();
      filter.Excepts = this.deliveryProducts()
        .map((line) => line.VehicleTypeUid)
        .filter((equipmentTypeUid): equipmentTypeUid is Guid => equipmentTypeUid != null && String(equipmentTypeUid) !== "");
      const rows = await firstValueFrom(this.fleet.filterEquipmentTypes(filter));
      this.equipmentTypeCatalogList.set(rows ?? []);
    } catch {
      this.equipmentTypeCatalogList.set([]);
    } finally {
      this.equipmentTypeCatalogLoading.set(false);
    }
  }

  private async loadFleetEquipmentCatalog(): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    const idx = this.activeStepIndex();
    const steps = buildTicketDemandWizardSteps(ticket);
    if (steps[idx]?.key !== "equipment-selection") {
      return;
    }
    if (ticket.TicketType?.LvfTicketArticleNature !== LvfTicketArticleNature.VEHICLE) {
      return;
    }

    this.fleetCatalogLoading.set(true);
    try {
      const filter = SearchFilterBase.CreateInstance();
      filter.Paginate = true;
      filter.Page = 1;
      filter.PageSize = 60;
      filter.OnlyActiveEntities = true;
      filter.UseCache = false;
      filter.SearchInput = this.fleetCatalogSearchSig().trim();
      filter.Excepts = this.deliveryProducts()
        .map((line) => line.VehicleUid)
        .filter((equipmentUid): equipmentUid is Guid => equipmentUid != null && String(equipmentUid) !== "");
      const rows = await firstValueFrom(this.fleet.filterEquipmentVehicles(filter));
      this.fleetCatalogList.set(rows ?? []);
    } catch {
      this.fleetCatalogList.set([]);
    } finally {
      this.fleetCatalogLoading.set(false);
    }
  }

  equipmentLineEnergyLabel(line: TicketDeliveryProduct): string {
    const vtUidRaw = line.EquipmentType?.Uid ?? line.VehicleType?.Uid ?? line.EquipmentTypeUid ?? line.VehicleTypeUid;
    const vtUid = vtUidRaw != null && String(vtUidRaw) !== "" ? String(vtUidRaw) : "";
    if (!vtUid) {
      return this.translateSvc.instant("app.shared.notprovided");
    }
    const energyRaw = this.equipmentHandlingEnergyByTypeUid()[vtUid];
    if (energyRaw == null || !Number.isFinite(energyRaw)) {
      return this.translateSvc.instant("app.shared.notprovided");
    }
    return this.translateHandlingEnergyNumeric(energyRaw);
  }

  async ensureEquipmentModelsForEnergyLoaded(): Promise<void> {
    const ticket = this.draft();
    if (ticket?.TicketType?.LvfTicketArticleNature !== LvfTicketArticleNature.VEHICLE_TYPE) {
      return;
    }
    const ticketUid = ticket.Uid != null ? String(ticket.Uid) : "";
    if (!ticketUid) {
      return;
    }
    if (this.equipmentEnergyModelsFetchedForTicketUid === ticketUid) {
      return;
    }

    this.equipmentModelsEnergyLoading.set(true);
    try {
      const filter = SearchFilterBase.CreateInstance();
      filter.Paginate = false;
      filter.Page = 1;
      filter.PageSize = 2000;

      let rawModels: IEquipmentModelInfo[];
      try {
        rawModels = await firstValueFrom(this.fleet.filterEquipmentModels(filter));
      } catch {
        rawModels = [];
      }
      const map: Record<string, number> = {};
      for (const raw of rawModels) {
        const equipmentModel = EquipmentModel.createInstance(EquipmentModel, raw);
        const typeUidRaw = (equipmentModel as any).TypeUid;
        const typeUid = typeUidRaw != null ? String(typeUidRaw) : "";
        if (!typeUid || map[typeUid] !== undefined) {
          continue;
        }
        if (equipmentModel.VehicleModelExtension?.LvfHandlingEnergyType == null) {
          continue;
        }
        map[typeUid] = equipmentModel.VehicleModelExtension.LvfHandlingEnergyType;
      }
      const cur = this.draft();
      if (!cur?.Uid || String(cur.Uid) !== ticketUid) {
        return;
      }
      this.equipmentHandlingEnergyByTypeUid.set(map);
      this.equipmentEnergyModelsFetchedForTicketUid = ticketUid;
    } finally {
      this.equipmentModelsEnergyLoading.set(false);
    }
  }

  private async enrichDraftEquipmentEmbeddedRefs(): Promise<void> {
    const ticket = this.draft();
    const nature = ticket?.TicketType?.LvfTicketArticleNature;
    if (!ticket?.Delivery?.Products?.length) {
      return;
    }
    if (nature !== LvfTicketArticleNature.VEHICLE && nature !== LvfTicketArticleNature.VEHICLE_TYPE) {
      return;
    }

    const lines = ticket.Delivery.Products;
    const equipmentUidSet = new Set<string>();
    const equipmentTypeUidSet = new Set<string>();
    const needsEquipmentHydration = (line: TicketDeliveryProduct): boolean => {
      const equipment = line.EquipmentVehicle;
      return (
        !equipment?.IdentificationInfo?.trim() &&
        !equipment?.Designation?.trim() &&
        !equipment?.RegistrationNumber?.trim() &&
        !equipment?.NumeroParc?.trim()
      );
    };
    const needsEquipmentTypeHydration = (line: TicketDeliveryProduct): boolean =>
      !line.EquipmentType?.Label?.trim() && !line.VehicleType?.Label?.trim();

    for (const line of lines) {
      if (line.VehicleUid != null && String(line.VehicleUid) !== "" && needsEquipmentHydration(line)) {
        equipmentUidSet.add(String(line.VehicleUid));
      }
      if (line.VehicleTypeUid != null && String(line.VehicleTypeUid) !== "" && needsEquipmentTypeHydration(line)) {
        equipmentTypeUidSet.add(String(line.VehicleTypeUid));
      }
    }

    if (!equipmentUidSet.size && !equipmentTypeUidSet.size) {
      return;
    }

    const ticketUid = ticket.Uid ?? null;

    const equipmentFilter = SearchFilterBase.CreateInstance();
    equipmentFilter.Paginate = false;
    if (equipmentUidSet.size) {
      equipmentFilter.EntityUids = [...equipmentUidSet] as unknown as Guid[];
    }

    const equipmentTypeFilter = SearchFilterBase.CreateInstance();
    equipmentTypeFilter.Paginate = false;
    if (equipmentTypeUidSet.size) {
      equipmentTypeFilter.EntityUids = [...equipmentTypeUidSet] as unknown as Guid[];
    }

    let equipmentRows: IEquipmentInfo[] = [];
    let equipmentTypeRows: IEquipmentTypeInfo[] = [];
    await Promise.all([
      (async (): Promise<void> => {
        if (!equipmentUidSet.size) return;
        try {
          equipmentRows = await firstValueFrom(this.fleet.filterEquipmentVehicles(equipmentFilter));
        } catch {
          equipmentRows = [];
        }
      })(),
      (async (): Promise<void> => {
        if (!equipmentTypeUidSet.size) return;
        try {
          equipmentTypeRows = await firstValueFrom(this.fleet.filterEquipmentTypes(equipmentTypeFilter));
        } catch {
          equipmentTypeRows = [];
        }
      })(),
    ]);

    const cur = this.draft();
    if (!cur?.Uid || (ticketUid != null && cur.Uid !== ticketUid)) {
      return;
    }
    const curLines = cur.Delivery?.Products ?? [];
    if (!curLines.length) {
      return;
    }

    const equipmentByUid = new Map<string, EquipmentVehicle>();
    for (const raw of equipmentRows) {
      const equipment = new EquipmentVehicle(raw);
      if (equipment.Uid != null && String(equipment.Uid) !== "") {
        equipmentByUid.set(String(equipment.Uid), equipment);
      }
    }
    const equipmentTypeByUid = new Map<string, EquipmentType>();
    for (const raw of equipmentTypeRows) {
      const equipmentType = EquipmentType.createInstance(EquipmentType, raw);
      if (equipmentType.Uid != null && String(equipmentType.Uid) !== "") {
        equipmentTypeByUid.set(String(equipmentType.Uid), equipmentType);
      }
    }

    const nextLines = curLines.map((line) => {
      let nextEquipment = line.EquipmentVehicle;
      let nextEquipmentType = line.EquipmentType ?? line.VehicleType;
      let changed = false;
      const equipmentUid = line.VehicleUid != null ? String(line.VehicleUid) : "";
      const equipmentTypeUid = line.VehicleTypeUid != null ? String(line.VehicleTypeUid) : "";

      if (equipmentUid && needsEquipmentHydration(line)) {
        const hydratedEquipment = equipmentByUid.get(equipmentUid);
        if (hydratedEquipment) {
          nextEquipment = hydratedEquipment;
          changed = true;
        }
      }
      if (equipmentTypeUid && needsEquipmentTypeHydration(line)) {
        const hydratedEquipmentType = equipmentTypeByUid.get(equipmentTypeUid);
        if (hydratedEquipmentType) {
          nextEquipmentType = hydratedEquipmentType;
          changed = true;
        }
      }
      if (!changed) {
        return line;
      }
      return TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
        ...(line as unknown as ITicketDeliveryProductInfo),
        EquipmentVehicle: nextEquipment,
        EquipmentType: nextEquipmentType,
        VehicleType: nextEquipmentType,
      } as ITicketDeliveryProductInfo);
    });
    if (!nextLines.some((nl, ix) => nl !== curLines[ix])) {
      return;
    }

    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, {
      ...(cur.Delivery as ITicketDeliveryInfo),
      Products: nextLines,
    } as ITicketDeliveryInfo);
    this.patchDraft({ Delivery: nextDelivery });
  }

  /** Entrées sur étapes equipment : hydratation des libellés. */
  async initializeEquipmentWizardStep(): Promise<void> {
    this.selectionHydrating.set(true);
    try {
      await this.enrichDraftEquipmentEmbeddedRefs();
    } finally {
      this.selectionHydrating.set(false);
    }
  }

  private sameDeliveryBasketLine(
    ref: TicketDeliveryProduct,
    refUidStr: string | null,
    target: TicketDeliveryProduct,
  ): boolean {
    if (refUidStr != null && target.Uid != null && String(target.Uid) !== "" && String(target.Uid) === refUidStr) {
      return true;
    }
    if (ref.ProductUid != null && String(ref.ProductUid) !== "") {
      return String(target.ProductUid ?? "") === String(ref.ProductUid);
    }
    const refEquipmentUidPresent = ref.VehicleUid != null && String(ref.VehicleUid) !== "";
    const refEquipmentTypeUidPresent =
      ref.VehicleTypeUid != null && String(ref.VehicleTypeUid) !== "";
    if (refEquipmentUidPresent && String(target.VehicleUid ?? "") === String(ref.VehicleUid)) {
      if (refEquipmentTypeUidPresent) {
        return String(target.VehicleTypeUid ?? "") === String(ref.VehicleTypeUid);
      }
      return true;
    }
    if (refEquipmentTypeUidPresent && String(target.VehicleTypeUid ?? "") === String(ref.VehicleTypeUid)) {
      return true;
    }
    return false;
  }

  isLastStep(): boolean {
    const steps = this.wizardSteps();
    return steps.length === 0 || this.activeStepIndex() >= steps.length - 1;
  }

  previous(): void {
    if (this.activeStepIndex() > 0) {
      this.activeStepIndex.update((stepIndex) => stepIndex - 1);
    }
  }

  next(): void {
    if (this.currentStepKey() === "general") {
      if (this.generalForm.invalid) {
        this.generalForm.markAllAsTouched();
        return;
      }
      const ticket = this.draft();
      if (ticket && !this.hasRequester(ticket)) {
        this.requesterMissingTouched.set(true);
        return;
      }
    }
    if (this.currentStepKey() === "products") {
      if (this.deliveryProducts().length === 0) {
        this.productsStepMissingLines.set(true);
        return;
      }
      this.productsStepMissingLines.set(false);
    }
    if (this.currentStepKey() === "equipment-types" || this.currentStepKey() === "equipment-selection") {
      if (this.deliveryProducts().length === 0) {
        this.equipmentLinesStepMissing.set(true);
        return;
      }
      this.equipmentLinesStepMissing.set(false);
    }
    if (this.currentStepKey() === "address") {
      if (!this.isTicketDeliveryAddressReady(this.draft())) {
        this.deliveryAddressIncompleteTouched.set(true);
        return;
      }
      this.deliveryAddressIncompleteTouched.set(false);
    }
    const steps = this.wizardSteps();
    const max = steps.length - 1;
    if (this.activeStepIndex() < max) {
      this.activeStepIndex.update((stepIndex) => stepIndex + 1);
    }
  }

  async save(): Promise<void> {
    await this.persistTicketAndNavigate("submit");
  }

  /** Aligné sur {@link TicketWizardComponent.onSaveAsDraft}. */
  async saveAsDraft(): Promise<void> {
    await this.persistTicketAndNavigate("draft");
  }

  private async persistTicketAndNavigate(mode: "submit" | "draft"): Promise<void> {
    if (mode === "submit" && !this.canSubmitTicket()) {
      return;
    }
    if (mode === "draft" && !this.ticketAccess().hasCreateAccess) {
      return;
    }
    const ticket = this.draft();
    if (!ticket?.Uid || this.saveStatus() === "saving") {
      return;
    }
    if (mode === "submit" && !this.hasRequester(ticket)) {
      this.requesterMissingTouched.set(true);
      return;
    }
    ticket.Label = this.generalForm.controls.label.value.trim();
    ticket.Description = this.generalForm.controls.description.value.trim();

    this.saveStatus.set("saving");
    try {
      const departingStepKey = mapMobileTicketWizardStepToWebKey(this.currentStepKey());
      const saved = await firstValueFrom(this.api.saveWizardDepartingStep(ticket, departingStepKey));
      if (!saved) {
        this.saveStatus.set("error");
        return;
      }

      if (mode === "submit") {
        const submitResponse = await firstValueFrom(this.api.submitWizard(ticket.Uid!));
        if (!submitResponse?.IsSuccessful || !submitResponse.Data) {
          this.saveStatus.set("error");
          return;
        }
        ticket.EntityStatusUid = submitResponse.Data.EntityStatusUid;
        if (submitResponse.Data.EntityStatus) {
          ticket.EntityStatus = EntityStatus.createInstance(
            EntityStatus,
            submitResponse.Data.EntityStatus,
          );
        }
      }

      this.draft.set(ticket);
      await this.refreshTicketStatuses(ticket);
      this.saveStatus.set("idle");
      await this.router.navigate([".."], { relativeTo: this.route });
    } catch {
      this.saveStatus.set("error");
    }
  }

  async backToList(): Promise<void> {
    await this.router.navigate([".."], { relativeTo: this.route });
  }

  private patchDraft(patch: Partial<ITicketInfo>): void {
    const cur = this.draft();
    if (!cur) {
      return;
    }
    const base = cur as unknown as ITicketInfo;
    const merged: ITicketInfo = { ...base, ...patch };
    if (patch.Delivery !== undefined) {
      merged.Delivery = {
        ...(base.Delivery ?? {}),
        ...(patch.Delivery ?? {}),
      } as ITicketInfo["Delivery"];
    }
    this.draft.set(Ticket.createInstance(Ticket, merged));
  }

  private clearRequesterSelectionForModeSwitch(): void {
    this.patchDraft({
      RequesterTeamUid: undefined,
      RequesterTeam: undefined,
      RequesterIdentityUid: undefined,
      RequesterIdentity: undefined,
      SubDivisionUid: undefined,
      SubDivision: undefined,
    });
  }

  private clearRecipientSelectionForModeSwitch(): void {
    const ticket = this.draft();
    if (!ticket) {
      return;
    }
    const merged = this.mergeDeliveryPatch(ticket.Delivery as unknown as ITicketDeliveryInfo | undefined, {
      RecipientTeamUid: undefined,
      RecipientTeam: undefined,
      RecipientIdentityUid: undefined,
      RecipientIdentity: undefined,
    });
    this.patchDraft({ Delivery: merged });
  }

  private mergeDeliveryPatch(
    prev: ITicketDeliveryInfo | undefined,
    patch: Partial<ITicketDeliveryInfo>
  ): ITicketDeliveryInfo {
    return { ...(prev ?? {}), ...patch };
  }

  private buildRefFilter(): SearchFilterBase {
    const filter = SearchFilterBase.CreateInstance();
    filter.Paginate = false;
    filter.OnlyActiveEntities = true;
    return filter;
  }

  private async ensureRequesterCatalogForMode(mode: RequesterMode): Promise<void> {
    if (this.requesterCatalogLoading()) {
      return;
    }
    if (mode === "teams" && this.requesterTeamsCatalog().length > 0) {
      return;
    }
    if (mode === "identities" && this.requesterIdentitiesCatalog().length > 0) {
      return;
    }
    if (mode === "subdivisions" && this.requesterSubDivisionsCatalog().length > 0) {
      return;
    }
    this.requesterCatalogLoading.set(true);
    try {
      const filter = this.buildRefFilter();
      if (mode === "teams") {
        const rows = await firstValueFrom(this.api.filterTeams(filter));
        this.requesterTeamsCatalog.set(rows ?? []);
      } else if (mode === "identities") {
        const rows = await firstValueFrom(this.api.filterIdentities(filter));
        this.requesterIdentitiesCatalog.set(rows ?? []);
      } else {
        const rows = await firstValueFrom(this.api.filterSubDivisions(filter));
        this.requesterSubDivisionsCatalog.set(rows ?? []);
      }
    } catch {
      if (mode === "teams") {
        this.requesterTeamsCatalog.set([]);
      } else if (mode === "identities") {
        this.requesterIdentitiesCatalog.set([]);
      } else {
        this.requesterSubDivisionsCatalog.set([]);
      }
    } finally {
      this.requesterCatalogLoading.set(false);
    }
  }

  private async ensureRecipientCatalogForMode(mode: RecipientMode): Promise<void> {
    if (this.recipientCatalogLoading()) {
      return;
    }
    if (mode === "teams" && this.recipientTeamsCatalog().length > 0) {
      return;
    }
    if (mode === "identities" && this.recipientIdentitiesCatalog().length > 0) {
      return;
    }
    this.recipientCatalogLoading.set(true);
    try {
      const filter = this.buildRefFilter();
      if (mode === "teams") {
        const rows = await firstValueFrom(this.api.filterTeams(filter));
        this.recipientTeamsCatalog.set(rows ?? []);
      } else {
        const rows = await firstValueFrom(this.api.filterIdentities(filter));
        this.recipientIdentitiesCatalog.set(rows ?? []);
      }
    } catch {
      if (mode === "teams") {
        this.recipientTeamsCatalog.set([]);
      } else {
        this.recipientIdentitiesCatalog.set([]);
      }
    } finally {
      this.recipientCatalogLoading.set(false);
    }
  }

  private filterTeamsBySearch(rows: ITeamInfo[], qRaw: string): ITeamInfo[] {
    const searchTerm = qRaw.trim().toLowerCase();
    let out = rows;
    if (searchTerm) {
      out = rows.filter((team) => (team.Name ?? "").toLowerCase().includes(searchTerm));
    }
    return [...out].sort((a, b) =>
      (a.Name ?? "").localeCompare(b.Name ?? "", undefined, { sensitivity: "base" })
    );
  }

  private filterIdentitiesBySearch(rows: IUserInfo[], qRaw: string): IUserInfo[] {
    const searchTerm = qRaw.trim().toLowerCase();
    let out = rows;
    if (searchTerm) {
      out = rows.filter((identity) => this.identityRowLabel(identity).toLowerCase().includes(searchTerm));
    }
    return [...out].sort((a, b) =>
      this.identityRowLabel(a).localeCompare(this.identityRowLabel(b), undefined, {
        sensitivity: "base",
      })
    );
  }

  private filterSubDivisionsBySearch(rows: ISubDivisionInfo[], qRaw: string): ISubDivisionInfo[] {
    const searchTerm = qRaw.trim().toLowerCase();
    let out = rows;
    if (searchTerm) {
      out = rows.filter((subDivision) => (subDivision.Name ?? "").toLowerCase().includes(searchTerm));
    }
    return [...out].sort((a, b) =>
      (a.Name ?? "").localeCompare(b.Name ?? "", undefined, { sensitivity: "base" })
    );
  }

  private syncSelectionModesFromTicket(ticket: Ticket): void {
    if (ticket.RequesterTeamUid) {
      this.requesterSelectionMode.set("teams");
    } else if (ticket.RequesterIdentityUid) {
      this.requesterSelectionMode.set("identities");
    } else if (ticket.SubDivisionUid) {
      this.requesterSelectionMode.set("subdivisions");
    } else {
      this.requesterSelectionMode.set("teams");
    }
    if (ticket.Delivery?.RecipientTeamUid) {
      this.recipientSelectionMode.set("teams");
    } else if (ticket.Delivery?.RecipientIdentityUid) {
      this.recipientSelectionMode.set("identities");
    } else {
      this.recipientSelectionMode.set("teams");
    }
  }

  private async loadTicket(uid: string | null): Promise<void> {
    if (!uid) {
      this.loadStatus.set("error");
      return;
    }
    this.unregisterMobileWizardNavigation();
    this.loadStatus.set("loading");
    this.saveStatus.set("idle");
    this.requesterMissingTouched.set(false);
    this.requesterSearchSig.set("");
    this.recipientSearchSig.set("");
    this.ticketWorkflowInfo.set(null);
    this.ticketStatusList.set([]);
    this.productUnitsTicketUid = null;
    this.productCatalogList.set([]);
    this.productCatalogTotalCount.set(0);
    this.productCatalogPage = 1;
    this.productRowDrafts.set({});
    this.productsStepMissingLines.set(false);
    this.productAddHintKey.set(null);
    this.productStepPanel.set("catalog");
    this.productDetailProductUid.set(null);
    this.productDetailPreview.set(null);
    this.productSearchSig.set("");
    this.deliveryAgenciesCatalog.set([]);
    this.deliveryAgencySearchSig.set("");
    this.deliverySelectedAgency.set(null);
    this.deliveryAddressIncompleteTouched.set(false);
    this.equipmentHandlingEnergyByTypeUid.set({});
    this.equipmentEnergyModelsFetchedForTicketUid = null;
    this.equipmentModelsEnergyLoading.set(false);
    this.equipmentLinesStepMissing.set(false);
    try {
      const info = await firstValueFrom(this.api.getTicket(uid as Guid));
      const ticket = Ticket.createInstance(Ticket, info);
      this.draft.set(ticket);
      this.syncSelectionModesFromTicket(ticket);
      if (ticket.Delivery?.Products?.length) {
        await this.enrichDraftDeliveryProducts();
      }
      await this.refreshTicketStatuses(ticket);
      this.generalForm.patchValue({
        label: ticket.Label ?? "",
        description: ticket.Description ?? "",
      });
      this.activeStepIndex.set(0);
      this.loadStatus.set("ready");
      this.registerMobileWizardNavigation(ticket);
      void this.ensureRequesterCatalogForMode(this.requesterSelectionMode());
    } catch {
      this.loadStatus.set("error");
    }
  }

  /** Même stratégie que {@link TicketWizardComponent} / `refreshTicketStatuses`. */
  private async fetchTicketWorkflowInfo(ticket: Ticket): Promise<IEntityStatusWorkflowInfo | null> {
    try {
      const wfUid =
        ticket.EntityStatus?.WorkflowUid ?? ticket.EntityStatusWorkflowUid;
      let workflowInfo: IEntityStatusWorkflowInfo | null = null;
      if (wfUid != null && String(wfUid) !== "") {
        workflowInfo = await firstValueFrom(this.api.getEntityStatusWorkflow(wfUid as Guid));
      }
      if (!workflowInfo?.Statuses?.length) {
        workflowInfo = await firstValueFrom(this.api.getEntityStatusWorkflowByModule(LvfAppModule.TICKET));
      }
      return workflowInfo?.Statuses?.length ? workflowInfo : null;
    } catch {
      return null;
    }
  }

  /** Aligné sur {@link TicketWizardComponent} `refreshTicketStatuses` + mémorise le WF pour `showSaveAsDraftButton`. */
  private async refreshTicketStatuses(ticket: Ticket): Promise<void> {
    const workflowInfo = await this.fetchTicketWorkflowInfo(ticket);
    this.ticketWorkflowInfo.set(workflowInfo);
    this.ticketStatusList.set(this.buildFullTicketStatusList(workflowInfo, ticket));
  }

  private buildFullTicketStatusList(
    workflow: IEntityStatusWorkflowInfo | null,
    ticket: Ticket
  ): IEntityStatusInfo[] {
    const raw = [...(workflow?.Statuses ?? [])];
    if (!raw.length) {
      return this.buildTicketStatusFallbackFromTransitions(ticket);
    }
    return this.mergeTicketStatusList(raw, ticket.EntityStatus);
  }

  private mergeTicketStatusList(
    workflowStatuses: IEntityStatusInfo[],
    current?: IEntityStatusInfo
  ): IEntityStatusInfo[] {
    const byUid = new Map<string, IEntityStatusInfo>();
    for (const status of workflowStatuses) {
      if (status.Uid != null && String(status.Uid) !== "") {
        byUid.set(String(status.Uid), status);
      }
    }
    if (current?.Uid != null && String(current.Uid) !== "" && !byUid.has(String(current.Uid))) {
      byUid.set(String(current.Uid), current);
    }
    return [...byUid.values()].sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
  }

  private buildTicketStatusFallbackFromTransitions(ticket: Ticket): IEntityStatusInfo[] {
    const out: IEntityStatusInfo[] = [];
    const seen = new Set<string>();
    const push = (status?: IEntityStatusInfo): void => {
      if (!status?.Uid) {
        return;
      }
      const statusUidKey = String(status.Uid);
      if (seen.has(statusUidKey)) {
        return;
      }
      seen.add(statusUidKey);
      out.push(status);
    };
    push(ticket.EntityStatus);
    for (const transition of ticket.EntityStatus?.Transitions ?? []) {
      push(transition.TargetStatus);
    }
    return out.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
  }

  private createDeliveryProductLine(
    product: IProductInfo,
    quantity: number,
    unit: IUnitInfo,
  ): TicketDeliveryProduct {
    return TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
      ProductUid: product.Uid,
      Product: Product.createInstance(Product, product),
      Quantity: quantity,
      UnitUid: unit.Uid,
      Unit: Unit.createInstance(Unit, unit),
    } as ITicketDeliveryProductInfo);
  }

  private mergeProductLineIntoDraft(line: TicketDeliveryProduct): void {
    const ticket = this.draft();
    if (!ticket) {
      return;
    }
    const prevDelivery = ticket.Delivery;
    const prevProducts = [...(prevDelivery?.Products ?? [])];
    const existingIndex = prevProducts.findIndex((productLine) => productLine.ProductUid === line.ProductUid);
    if (existingIndex >= 0) {
      prevProducts[existingIndex] = line;
    } else {
      prevProducts.push(line);
    }
    const baseInfo: ITicketDeliveryInfo = {
      ...((prevDelivery ?? {}) as ITicketDeliveryInfo),
      Products: prevProducts,
    };
    const nextDelivery = TicketDelivery.createInstance(TicketDelivery, baseInfo);
    this.patchDraft({ Delivery: nextDelivery });
  }

  private async prepareProductSelectionPanel(): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    const uid = String(ticket.Uid);
    if (this.productUnitsTicketUid !== uid) {
      await this.loadUnitsCatalog();
      if (this.draft()?.Uid !== ticket.Uid) {
        return;
      }
      this.productUnitsTicketUid = uid;
    }
    await this.enrichDraftDeliveryProducts();
  }

  /**
   * Comme le web (`TicketCreationSummaryComponent.enrichProducts`) : après chargement API,
   * les lignes de livraison n’ont souvent que `ProductUid` / `UnitUid` sans objets imbriqués.
   */
  private async enrichDraftDeliveryProducts(): Promise<void> {
    const ticket0 = this.draft();
    if (!ticket0?.Delivery?.Products?.length) {
      return;
    }
    this.selectionHydrating.set(true);
    try {
      await this.enrichDraftEquipmentEmbeddedRefs();
      const ticket = this.draft();
      if (!ticket?.Delivery?.Products?.length) {
        return;
      }
      const lines = ticket.Delivery.Products;
      const productUidSet = new Set<string>();
      const unitUidSet = new Set<string>();
      let hasLabelOnlyLines = false;
      for (const line of lines) {
        const storedLabel = line.ProductLabel?.trim() || line.Comment?.trim();
        if (storedLabel && !line.Product?.Label?.trim() && !line.ProductLabel?.trim()) {
          hasLabelOnlyLines = true;
        }
        if (
          line.ProductUid != null &&
          String(line.ProductUid) !== "" &&
          !line.Product?.Label?.trim()
        ) {
          productUidSet.add(String(line.ProductUid));
        }
        if (line.UnitUid != null && String(line.UnitUid) !== "" && !line.Unit?.Name?.trim()) {
          unitUidSet.add(String(line.UnitUid));
        }
      }
      if (!productUidSet.size && !unitUidSet.size && !hasLabelOnlyLines) {
        return;
      }
      const ticketUid = ticket.Uid;
      const fProd = SearchFilterBase.CreateInstance();
      fProd.Paginate = false;
      fProd.OnlyActiveEntities = true;
      if (productUidSet.size) {
        fProd.EntityUids = [...productUidSet] as unknown as Guid[];
      }
      const fUnit = SearchFilterBase.CreateInstance();
      fUnit.Paginate = false;
      fUnit.OnlyActiveEntities = true;
      if (unitUidSet.size) {
        fUnit.EntityUids = [...unitUidSet] as unknown as Guid[];
      }
      const [productRows, unitRows] = await Promise.all([
        productUidSet.size
          ? firstValueFrom(this.api.filterProducts(fProd))
              .then((r) => r.items)
              .catch(() => [] as IProductInfo[])
          : Promise.resolve<IProductInfo[]>([]),
        unitUidSet.size ? firstValueFrom(this.api.filterUnits(fUnit)).catch(() => []) : Promise.resolve<IUnitInfo[]>([]),
      ]);
      const cur = this.draft();
      if (!cur?.Uid || String(cur.Uid) !== String(ticketUid)) {
        return;
      }
      const curLines = cur.Delivery?.Products ?? [];
      if (!curLines.length) {
        return;
      }
      const byProductUid = new Map<string, Product>();
      for (const raw of productRows ?? []) {
        const product = Product.createInstance(Product, raw);
        if (product.Uid != null && String(product.Uid) !== "") {
          byProductUid.set(String(product.Uid), product);
        }
      }
      const byUnitUid = new Map<string, Unit>();
      for (const raw of unitRows ?? []) {
        const unit = Unit.createInstance(Unit, raw);
        if (unit.Uid != null && String(unit.Uid) !== "") {
          byUnitUid.set(String(unit.Uid), unit);
        }
      }
      const nextLines = curLines.map((line) => {
        const productUid = line.ProductUid != null ? String(line.ProductUid) : "";
        const unitUid = line.UnitUid != null ? String(line.UnitUid) : "";
        const storedLabel = line.ProductLabel?.trim() || line.Comment?.trim() || null;
        let nextProduct = line.Product;
        let nextProductLabel = line.ProductLabel?.trim() || line.Comment?.trim() || null;
        let nextUnit = line.Unit;
        let lineChanged = false;
        if (productUid && byProductUid.has(productUid) && !line.Product?.Label?.trim()) {
          nextProduct = byProductUid.get(productUid)!;
          nextProductLabel = nextProductLabel ?? nextProduct.Label ?? null;
          lineChanged = true;
        } else if (!productUid && storedLabel && !line.Product?.Label?.trim()) {
          nextProduct = Product.createInstance(Product, { Label: storedLabel } as IProductInfo);
          nextProductLabel = storedLabel;
          lineChanged = true;
        } else if (storedLabel && !nextProductLabel) {
          nextProductLabel = storedLabel;
          lineChanged = true;
        }
        if (unitUid && byUnitUid.has(unitUid) && !line.Unit?.Name?.trim()) {
          nextUnit = byUnitUid.get(unitUid)!;
          lineChanged = true;
        }
        if (!lineChanged) {
          return line;
        }
        return TicketDeliveryProduct.createInstance(TicketDeliveryProduct, {
          ...(line as unknown as ITicketDeliveryProductInfo),
          Product: nextProduct,
          ProductLabel: nextProductLabel,
          Unit: nextUnit,
        } as ITicketDeliveryProductInfo);
      });
      if (!nextLines.some((nl, i) => nl !== curLines[i])) {
        return;
      }
      const nextDelivery = TicketDelivery.createInstance(TicketDelivery, {
        ...(cur.Delivery as ITicketDeliveryInfo),
        Products: nextLines,
      } as ITicketDeliveryInfo);
      this.patchDraft({ Delivery: nextDelivery });
    } finally {
      this.selectionHydrating.set(false);
    }
  }

  private async initializeProductsStep(): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    const uid = String(ticket.Uid);
    if (this.productUnitsTicketUid !== uid) {
      await this.loadUnitsCatalog();
      if (this.draft()?.Uid !== ticket.Uid) {
        return;
      }
      this.productUnitsTicketUid = uid;
    }
    await this.loadProductCatalog();
  }

  private async loadUnitsCatalog(): Promise<void> {
    try {
      const filter = SearchFilterBase.CreateInstance();
      filter.Paginate = false;
      filter.OnlyActiveEntities = true;
      filter.UseCache = false;
      const rows = await firstValueFrom(this.api.filterUnits(filter));
      this.unitsCatalog.set(rows ?? []);
    } catch {
      this.unitsCatalog.set([]);
    }
  }

  private async loadProductCatalog(): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    const idx = this.activeStepIndex();
    const stepKey = buildTicketDemandWizardSteps(ticket)[idx]?.key;
    if (stepKey !== "products") {
      return;
    }
    this.productCatalogPage = 1;
    await this.fetchProductCatalog(true);
  }

  private async fetchProductCatalog(reset: boolean): Promise<void> {
    const ticket = this.draft();
    if (!ticket?.Uid) {
      return;
    }
    if (reset) {
      this.productCatalogLoading.set(true);
    } else {
      this.productCatalogLoadingMore.set(true);
    }
    try {
      const filter = SearchFilterBase.CreateInstance();
      filter.Paginate = true;
      filter.Page = 1;
      filter.PageSize = 60;
      filter.OnlyActiveEntities = true;
      filter.UseCache = false;
      filter.SearchInput = this.productSearchSig().trim();
      filter.Excepts = this.deliveryProducts()
        .map((line) => line.ProductUid)
        .filter((productUid): productUid is Guid => productUid != null && String(productUid) !== "");
      const natures = this.lvfProductNaturesForTicket(ticket);
      if (natures.length > 0) {
        filter.LvfProductNatures = natures;
      }
      const rows = await firstValueFrom(this.api.filterProducts(filter));
      const list = rows?.items ?? [];
      this.productCatalogList.set(list);
      this.productCatalogTotalCount.set(rows?.totalCount ?? list.length);
      this.ensureRowDraftDefaultsFor(list);
    } catch {
      if (reset) {
        this.productCatalogList.set([]);
        this.productCatalogTotalCount.set(0);
      }
    } finally {
      this.productCatalogLoading.set(false);
      this.productCatalogLoadingMore.set(false);
    }
  }

  private ensureRowDraftDefaultsFor(products: IProductInfo[]): void {
    this.productRowDrafts.update((cur) => {
      const next = { ...cur };
      for (const product of products) {
        const productUid = String(product.Uid ?? "");
        if (!productUid || next[productUid]) {
          continue;
        }
        next[productUid] = { quantity: 1, unitUid: "" };
      }
      return next;
    });
  }

  private lvfProductNaturesForTicket(ticket: Ticket): LvfProductNature[] {
    const nature =
      ticket.TicketType?.LvfTicketArticleNature ?? LvfTicketArticleNature.PERISHABLE_PRODUCT;
    const map: Partial<Record<LvfTicketArticleNature, LvfProductNature | null>> = {
      [LvfTicketArticleNature.PERISHABLE_PRODUCT]: LvfProductNature.Perishable,
      [LvfTicketArticleNature.SERVICE]: LvfProductNature.Service,
      [LvfTicketArticleNature.VEHICLE]: null,
      [LvfTicketArticleNature.VEHICLE_TYPE]: null,
    };
    const mappedNature = map[nature];
    return mappedNature ? [mappedNature] : [];
  }
}
